import ColorConverter from '@sosocio/color-converter';
import {
	EditorVectorEditColorsImageVectorColor,
	EditorVectorEditColorsImageVectorColors,
	LogoColorsResult,
} from 'interfaces/app';
import { OfferingModel } from 'interfaces/database';
import {
	PageObjectModel,
	PageObjectVectorColor,
} from 'interfaces/project';
import * as projectTools from './project';

type GetPantoneNameOptions = (
	{
		name: string;
	}
	| {
		hex: string;
	}
);

export function check(
	offeringModel: OptionalExceptFor<OfferingModel, 'previewColorSpectrum'>,
	objectModel: OptionalExceptFor<PageObjectModel, 'colorReplacement'>,
): LogoColorsResult {
	const logoColors = getUniqueUsedColors(objectModel).size;

	return {
		logoColors,
		offeringColors: projectTools.getOfferingModelColorQuantity(offeringModel),
		valid: logoColors <= projectTools.getOfferingModelColorQuantity(offeringModel),
	};
}

export function getColors(objectModel: OptionalExceptFor<PageObjectModel, 'colorReplacement' | '_vectorColors'>): EditorVectorEditColorsImageVectorColors {
	if (!objectModel._vectorColors) {
		return {
			foreground: [],
		};
	}

	const vectorColorsTypeKeys = Object.keys(objectModel._vectorColors) as Array<keyof EditorVectorEditColorsImageVectorColors>;
	const { _vectorColors } = objectModel;

	return vectorColorsTypeKeys.reduce(
		(photoVectorColors, vectorColorsTypeKey) => {
			let vectorColorsKeys: string[] = [];
			const vectorColorsType = _vectorColors[vectorColorsTypeKey];

			if (vectorColorsType instanceof Map) {
				vectorColorsKeys = Array.from(vectorColorsType.keys());
			} else if (vectorColorsType) {
				vectorColorsKeys = [vectorColorsType.color];
			}

			for (const vectorColorKey of vectorColorsKeys) {
				let vectorColor: PageObjectVectorColor | undefined;

				if (vectorColorsType instanceof Map) {
					vectorColor = vectorColorsType.get(vectorColorKey);
				} else if (vectorColorsType?.color === vectorColorKey) {
					vectorColor = vectorColorsType;
				}

				if (vectorColor) {
					const photoVectorColor: EditorVectorEditColorsImageVectorColor = {
						color: vectorColorKey,
						matches: vectorColor.matches,
					};

					if (vectorColorsTypeKey === 'foreground') {
						photoVectorColors[vectorColorsTypeKey].push(photoVectorColor);
					} else {
						photoVectorColors[vectorColorsTypeKey] = photoVectorColor;
					}

					const colorReplacementFound = objectModel.colorReplacement?.find((colorReplacement) => colorReplacement.color === vectorColorKey);

					if (colorReplacementFound) {
						photoVectorColor.replace = {
							...colorReplacementFound.replace,
						};
					}
				}
			}

			return photoVectorColors;
		},
		{
			foreground: [],
		} as EditorVectorEditColorsImageVectorColors,
	);
}

/**
 * Get the formatted Pantone color based on either a raw
 * Pantone color name (179 C) or a hex6 color (#ff0000)
 * @param options.name Raw pantone color name to format, e.g *179 C*
 * @param options.hex Hex6 color to get the Pantone color name from, e.g. *#ff0000*
 * @returns Except for *White*, everything else will be
 * formatted as **PANTONE** `{name}`
 */
export function getPantoneName(options: GetPantoneNameOptions): string | undefined {
	if ('name' in options) {
		return (options.name !== 'White'
			? `PANTONE ${options.name}`
			: options.name
		);
	}
	if ('hex' in options) {
		try {
			const colorConverter = new ColorConverter();
			colorConverter.hex6 = {
				value: (
					options.hex.startsWith('#')
						? options.hex.slice(1)
						: options.hex
				).toLowerCase(),
			};
			colorConverter.pantone = {
				name: colorConverter.pantone.name,
			};

			return (
				getPantoneName({
					name: colorConverter.pantone.name,
				})
				|| undefined
			);
		} catch {
			// Do nothing, fallback to `undefined`
		}
	}

	return undefined;
}

export function getUniqueUsedColors(objectModel: OptionalExceptFor<PageObjectModel, 'colorReplacement'>): Set<string> {
	return new Set(
		objectModel?.colorReplacement
			?.map((vectorColor) => (
				vectorColor.replace?.real.toLocaleLowerCase()
				|| vectorColor.color.toLocaleLowerCase()
			))
			.filter((color) => color !== 'transparent'),
	);
}
